/**
 * TeamFormSlideout - Create or edit an ahoy_team via CRM events.
 *
 * Combines team details (name, description) and member assignment into a
 * single slideout. All changes are batched into one event submission.
 *
 * CREATE mode: editingTeam is null/undefined → builds objectCreated event
 *   plus any associationAdded events for selected members.
 * EDIT mode: editingTeam provided → builds propertiesUpdated event for changed
 *   fields, plus associationAdded/Removed for member changes. If nothing
 *   changed, closes without submitting.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { SlideoutMenu } from '@/lib/base/overlays/slideout-menu';

import { FormProvider, TeamFormContent } from './TeamFormMembersSection';
import { useTeamMemberSelection } from './useTeamMemberSelection';
import { useTeamSave } from './useTeamSave';

const noop = () => {};

// =============================================================================
// Types
// =============================================================================

export interface TeamFormSlideoutProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: number;
  /** When provided, the slideout opens in edit mode. */
  editingTeam?: {
    entity_id: string;
    display_name: string;
    description: string;
    last_event_seq: number;
  } | null;
  /** Called after a successful save so the parent can invalidate caches. */
  onSaved?: () => void;
}

// =============================================================================
// TeamFormSlideout — main component
// =============================================================================

export function TeamFormSlideout({
  isOpen,
  onOpenChange,
  orgId,
  editingTeam,
  onSaved,
}: TeamFormSlideoutProps) {
  const { user } = useAuthInfo();

  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');

  const {
    selectedUserIds,
    initialMemberUserIds,
    isMemberIdsLoading,
    isMemberIdsError,
    handleToggleMember,
  } = useTeamMemberSelection({ isOpen, editingTeam, orgId });

  // Reset form values when switching teams or toggling create/edit mode.
  // Keyed by entity_id so initial values are re-derived only when the team
  // changes, not on every parent re-render that updates display_name/description.
  const initialValues = useMemo(
    () => ({
      displayName: editingTeam?.display_name ?? '',
      description: editingTeam?.description ?? '',
    }),
    // Intentionally depends only on entity_id — changing the team identity
    // (or switching create↔edit) resets the form; incremental prop updates do not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingTeam?.entity_id]
  );

  useEffect(() => {
    if (isOpen) {
      setDisplayName(initialValues.displayName);
      setDescription(initialValues.description);
    }
  }, [isOpen, initialValues]);

  const title = editingTeam ? 'Edit team' : 'Create team';
  const submitLabel = editingTeam ? 'Save changes' : 'Create team';

  const fieldConfig = useMemo(
    () => ({
      displayName: {
        validate: (v: unknown) => {
          const trimmed = String(v ?? '').trim();
          if (!trimmed) {
            return 'Team name is required';
          }
          if (trimmed.length > 200) {
            return 'Team name must be 200 characters or less';
          }
          return undefined;
        },
      },
      description: {
        validate: (v: unknown) => {
          if (String(v ?? '').length > 1000) {
            return 'Description must be 1000 characters or less';
          }
          return undefined;
        },
      },
    }),
    []
  );

  const formValues = useMemo(
    () => ({ displayName, description }),
    [displayName, description]
  );

  const fieldLabels = useMemo(
    () => ({ displayName: 'Team name', description: 'Description' }),
    []
  );

  const { isSaving, handleSave } = useTeamSave({
    editingTeam,
    displayName,
    description,
    userId: String(user.id),
    orgId,
    onOpenChange,
    onSaved,
    selectedUserIds,
    initialMemberUserIds,
  });

  const handleDisplayNameChange = useCallback(
    (v: string) => setDisplayName(v),
    []
  );
  const handleDescriptionChange = useCallback(
    (v: string) => setDescription(v),
    []
  );

  // Dirty check for the unsaved-changes guard: the live form against the values
  // the slideout opened with. In create mode that baseline is empty; in edit
  // mode it is the team's saved name, description and member set.
  const hasUnsavedChanges = useMemo(() => {
    if (
      displayName.trim() !== initialValues.displayName.trim() ||
      description.trim() !== initialValues.description.trim()
    ) {
      return true;
    }
    if (selectedUserIds.size !== initialMemberUserIds.length) {
      return true;
    }
    return initialMemberUserIds.some(id => !selectedUserIds.has(id));
  }, [
    displayName,
    description,
    initialValues,
    selectedUserIds,
    initialMemberUserIds,
  ]);

  // Raw close: the chrome (SlideoutMenu) owns the unsaved-changes guard via
  // `hasUnsavedChanges`/`onDiscardUnsavedChanges` below, so this only needs
  // to forward the close-intent. A successful save closes via `onOpenChange`
  // directly (see useTeamSave), which passes straight through here too.
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onOpenChange(false);
      }
    },
    [onOpenChange]
  );

  // The key forces FormProvider to remount when switching teams or toggling
  // create/edit mode, which automatically resets touched state.
  const formKey = editingTeam?.entity_id ?? 'create';

  return (
    <SlideoutMenu
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      dialogAriaLabel={title}
      hasUnsavedChanges={hasUnsavedChanges}
      onDiscardUnsavedChanges={noop}
    >
      {({ close }) => (
        <FormProvider
          key={formKey}
          fields={fieldConfig}
          values={formValues}
          fieldLabels={fieldLabels}
        >
          <TeamFormContent
            title={title}
            submitLabel={submitLabel}
            displayName={displayName}
            onDisplayNameChange={handleDisplayNameChange}
            description={description}
            onDescriptionChange={handleDescriptionChange}
            isSaving={isSaving}
            onClose={close}
            onSave={handleSave}
            orgId={orgId}
            selectedUserIds={selectedUserIds}
            onToggleMember={handleToggleMember}
            isMemberIdsLoading={isMemberIdsLoading}
            initialMemberIdsError={isMemberIdsError}
          />
        </FormProvider>
      )}
    </SlideoutMenu>
  );
}
